import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils.js";

/**
 * One of the two guess grids — artist or title.
 *
 * # The grid legitimately contains the right answer
 *
 * That is what makes it a guess. What must never arrive before `Reveal` is
 * *which* one — so `correctOption` is `null` until the server sends an
 * `AnswerView`, and this component has no other way to learn it. Getting that
 * backwards is the single mistake that makes the game trivially cheatable, and
 * `tests/game_no_leak.rs` plus the transport test hold the server side of it.
 *
 * # A struck-out option stays on screen
 *
 * `narrow_options` removes a wrong answer. It is *shown* struck through and
 * disabled rather than deleted, because a grid that silently shrinks from four
 * to three makes a player lose their place — and the whole value of the power is
 * watching a wrong answer die.
 */
export function OptionGrid({
  label,
  options,
  selected = null,
  removed = [],
  correctOption = null,
  disabled = false,
  onSelect,
  className,
}: {
  /** What is being guessed — "Artist" or "Title". */
  label: string;
  /** The options, in the order the server shuffled them. */
  options: readonly string[];
  /** Which one this player has chosen. */
  selected?: number | null;
  /** Indices `narrow_options` struck out for this side. */
  removed?: readonly number[];
  /** Which one was right. **`null` until `Reveal`.** */
  correctOption?: number | null;
  /** Whether the grid is closed — wrong phase, or already locked in. */
  disabled?: boolean;
  /** Choose an option. Absent means read-only. */
  onSelect?: (index: number) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const revealed = correctOption !== null;

  return (
    <fieldset
      data-testid="option-grid"
      className={cn("min-w-0", className)}
      disabled={disabled || !onSelect}
    >
      <legend className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-white/50">
        {label}
      </legend>

      <div className="grid grid-cols-2 gap-2">
        {options.map((option, index) => {
          const struck = removed.includes(index);
          const isSelected = selected === index;
          const isCorrect = revealed && correctOption === index;
          // Only worth marking as wrong if they picked it. Painting every wrong
          // option red turns the reveal into a wall of failure.
          const isWrongPick = revealed && isSelected && !isCorrect;

          // Stated as a label rather than as a trailing `sr-only` span: the
          // accessible-name algorithm trims each text node before joining them,
          // so "Suzanne Vega" plus " — correct answer" is announced as
          // "Suzanne Vega— correct answer". An explicit label also keeps the
          // visible text as the *start* of the name, which is what voice control
          // matches on.
          // The dash lives here rather than in the message, so no translation
          // depends on leading whitespace surviving a JSON file.
          const suffix = isCorrect
            ? t("optionGrid.aria.correct")
            : isWrongPick
              ? t("optionGrid.aria.wrongPick")
              : struck
                ? t("optionGrid.aria.ruledOut")
                : "";

          return (
            <button
              key={`${index}-${option}`}
              type="button"
              aria-label={suffix ? `${option} — ${suffix}` : undefined}
              // A grid is a set of choices, not a set of independent buttons —
              // this is what tells a screen reader that picking one unpicks the
              // others.
              aria-pressed={isSelected}
              disabled={struck || disabled || !onSelect}
              onClick={onSelect ? () => onSelect(index) : undefined}
              className={cn(
                "min-h-14 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors",
                "disabled:cursor-not-allowed",
                struck && "border-white/5 text-white/25 line-through",
                !struck && isSelected && "border-flash-500 bg-flash-500/15",
                !struck &&
                  !isSelected &&
                  "border-white/12 bg-stage-900 hover:border-white/25",
                isCorrect && "border-correct-500 bg-correct-500/15",
                isWrongPick && "border-wrong-500 bg-wrong-500/15",
              )}
            >
              <span className="line-clamp-2">{option}</span>

              {/* Words on screen too, not just colour and not just a label — a
                  reveal that only recolours is unreadable to a chunk of any
                  party. */}
              {isCorrect && (
                <span className="mt-1 block text-[11px] font-semibold uppercase tracking-wide text-correct-500">
                  {t("optionGrid.correct")}
                </span>
              )}
              {isWrongPick && (
                <span className="mt-1 block text-[11px] font-semibold uppercase tracking-wide text-wrong-500">
                  {t("optionGrid.yourAnswer")}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
